import { NullableDateToIsoString } from '@mntn/api-contracts/_shared/date-to-iso-string'
import { PageResponseSchema } from '@mntn/api-contracts/_shared/page-response'
import { PaginationQuerySchema } from '@mntn/api-contracts/_shared/pagination'
import {
  Actors as A,
  authorizeAdvertiserIdBodyTransform,
  createAuthorizeAdvertiserIdTransform,
  Tenants as T,
} from '@mntn/auth-types'
import { ErrorSchema as E } from '@mntn/result'
import {
  CoerceNumberSchema,
  PositiveDecimalSchema,
  QueryIDSchema,
  QueryIDsSchema,
  RequiredCoerceDateSchema,
  WholeNumberSchema,
} from '@mntn/zod'
import { z } from 'zod'

// Inlined to avoid pulling @mntn/gary-client into the contracts package.
// Mirrors Gary's campaign-group action values without depending on the Gary client.
const CampaignGroupActionSchema = z.union([
  z.literal('ARCHIVE'),
  z.literal('DUPLICATE'),
  z.literal('DUPLICATE_AS_EXPERIMENT'),
  z.literal('VIEW'),
])

// Mirrors the campaign status values in the campaigns contract so this
// contract remains self-contained.
const CampaignGroupPublicStatusSchema = z.union([
  z.literal('ACTION_REQ'),
  z.literal('ARCHIVED'),
  z.literal('ENDED'),
  z.literal('INCOMPLETE'),
  z.literal('LEGACY_ARCHIVED'),
  z.literal('LIVE'),
  z.literal('PAUSED'),
  z.literal('READY'),
  z.literal('SCHEDULED'),
])

const OfferingTypeSchema = z.union([z.literal('deal'), z.literal('bundle')])

const OfferingTypesQuerySchema = z
  .union([z.array(OfferingTypeSchema), OfferingTypeSchema])
  .transform(types => (Array.isArray(types) ? types : [types]))
  .optional()

export const BaseQuerySchema = z.object({
  'advertiser-id': QueryIDSchema,
  'order-item-id': QueryIDsSchema.optional(),
  'campaign-group-id': QueryIDsSchema.optional(),
})

export const OrderItemsSortSchema = z.object({
  order: z
    .union([z.literal('asc'), z.literal('desc')])
    .default('desc')
    .optional(),
  'order-by': z
    .union([z.literal('expiration'), z.literal('name'), z.literal('search'), z.literal('created')])
    .default('created')
    .optional(),
})

export const OrderItemsFilterSchema = z.object({
  status: z.union([z.literal('upcoming'), z.literal('live'), z.literal('completed')]).optional(),
  'campaign-group-id': QueryIDsSchema.optional(),
  'genre-ids': QueryIDsSchema.optional(),
  'network-ids': QueryIDsSchema.optional(),
  'offering-types': OfferingTypesQuerySchema,
  'cpm-min': CoerceNumberSchema.pipe(PositiveDecimalSchema).optional(),
  'cpm-max': CoerceNumberSchema.pipe(PositiveDecimalSchema).optional(),
  'air-dates-start': RequiredCoerceDateSchema.optional(),
  'air-dates-end': RequiredCoerceDateSchema.optional(),
})

export const OrderItemsGetRequestSchema = BaseQuerySchema.merge(PaginationQuerySchema)
  .merge(OrderItemsSortSchema)
  .merge(OrderItemsFilterSchema)
  .extend({
    search: z.string().optional(),
  })

const AdvertiserUserPrincipalSchema = T.KindSchema('advertiser').and(A.UserSchema)

export const ListOrderItemsRequestSchema = z
  .object({
    query: OrderItemsGetRequestSchema,
    principal: AdvertiserUserPrincipalSchema,
  })
  .transform(
    createAuthorizeAdvertiserIdTransform(
      request => request.query['advertiser-id'],
      (request, advertiserId) => ({
        ...request,
        query: { ...request.query, 'advertiser-id': advertiserId },
      }),
    ),
  )

export const OrderItemsSummaryRequestSchema = OrderItemsFilterSchema.extend({
  'advertiser-id': QueryIDSchema,
  search: z.string().optional(),
})

export const GetOrderItemsSummaryRequestSchema = z
  .object({
    query: OrderItemsSummaryRequestSchema,
    principal: AdvertiserUserPrincipalSchema,
  })
  .transform(
    createAuthorizeAdvertiserIdTransform(
      request => request.query['advertiser-id'],
      (request, advertiserId) => ({
        ...request,
        query: { ...request.query, 'advertiser-id': advertiserId },
      }),
    ),
  )

const ProgramSchema = z.object({
  programId: z.number(),
  programName: z.string(),
  artwork: z.object({ croppedUrl: z.string() }),
  description: z.string(),
  network: z.object({
    name: z.string(),
    logo: z.object({ croppedUrl: z.string() }),
  }),
})

export const BaseOrderItemSchema = z.object({
  orderItemId: WholeNumberSchema,
  orderId: WholeNumberSchema,
  advertiserId: WholeNumberSchema,
  isTest: z.boolean(),
  publicId: z.uuid(),
  offeringId: WholeNumberSchema,
  offeringName: z.string(),
  offeringType: OfferingTypeSchema,
  offeringDescription: z.string(),
  coverMediaUrl: z.string().nullable(),
  programs: z.array(ProgramSchema),
  privateMarketplaceGroupId: z.number().nullable(),
  campaignTemplateGroupId: z.number(),
  audienceId: z.number().nullable(),
  predictionId: z.uuid().nullable(),
  availableImpressions: z.number(),
  totalImpressions: z.number(),
  targetCpm: z.number(),
  adBuyingCpm: z.number(),
  airStartTime: NullableDateToIsoString,
  airEndTime: NullableDateToIsoString,
  flightStartTime: z
    .union([z.string(), z.date(), z.null()])
    .transform(v => (v == null ? null : typeof v === 'string' ? v : v.toISOString()))
    .nullable()
    .optional(),
  flightEndTime: z
    .union([z.string(), z.date(), z.null()])
    .transform(v => (v == null ? null : typeof v === 'string' ? v : v.toISOString()))
    .nullable()
    .optional(),
  runStatus: z.union([
    z.literal('PENDING'),
    z.literal('IN_FLIGHT'),
    z.literal('EXPIRING_SOON'),
    z.literal('COMPLETED'),
  ]),
  configOverrideId: z.number().nullable(),
  totalCampaignGroupsCreated: z.number(),
})

const MetricsSchema = z.object({
  unallocated: z.number(),
  delivered: z.number(),
  ineffective: z.number(),
  effective: z.number(),
  allocatableEffective: z.number(),
  total: z.number(),
})

export const OrderItemMetricsSchema = z.object({
  budgetMetrics: MetricsSchema,
  impressionMetrics: MetricsSchema,
})
export type OrderItemMetrics = z.infer<typeof OrderItemMetricsSchema>

const UICreativesStatusSchema = z
  .object({
    status: z.union([
      z.literal('BAD'),
      z.literal('DONE'),
      z.literal('ESCALATE'),
      z.literal('NEEDS_APPROVAL'),
      z.literal('NOT_DONE'),
      z.literal('OPT_OUT'),
      z.literal('PROCESSING'),
    ]),
    details: z.string().nullable(),
    error: z.string().nullable(),
    warning: z.string().nullable(),
    action: z.string().nullable(),
  })
  .nullable()

const OrderItemCampaignGroupSchema = z.object({
  campaignGroupId: z.number(),
  advertiserId: z.number(),
  name: z.string(),
  ineffectiveBudget: z.number().nullable(),
  effectiveBudget: z.number().nullable(),
  allocatableEffectiveBudget: z.number().nullable(),
  totalBudget: z.number().nullable(),
  spend: z.number().nullable(),
  totalImpressions: z.number().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  status: CampaignGroupPublicStatusSchema,
  orderItemId: z.number().nullable(),
  configOverrideId: z.number().nullable(),
  availableActions: z.array(CampaignGroupActionSchema).optional(),
  uiCreativesStatus: UICreativesStatusSchema,
  hasSpendError: z.boolean().default(false),
})

export const OrderItemSchema = BaseOrderItemSchema.extend({
  budgetMetrics: MetricsSchema,
  impressionMetrics: MetricsSchema,
  campaignGroups: z.array(OrderItemCampaignGroupSchema).default([]),
})

export const OrderItemsGetResponseSchema = z.object({
  data: z.array(OrderItemSchema),
  page: PageResponseSchema,
})

export const OrderItemSummarySchema = z.object({
  impressions: z.object({ effective: z.number(), total: z.number() }),
  budget: z.object({
    effective: z.number(),
    total: z.number(),
    delivered: z.number(),
  }),
  upcomingDeals: z.object({ effective: z.number(), total: z.number() }),
  liveCampaigns: z.number(),
})

export const OrderItemSummaryResponseSchema = z.object({
  data: OrderItemSummarySchema,
})

export const OrderItemsStatusSummaryRequestSchema = BaseQuerySchema.extend({
  'advertiser-id': QueryIDSchema,
  search: z.string().optional(),
})

export const GetOrderItemsStatusSummaryRequestSchema = z
  .object({
    query: OrderItemsStatusSummaryRequestSchema,
    principal: AdvertiserUserPrincipalSchema,
  })
  .transform(
    createAuthorizeAdvertiserIdTransform(
      request => request.query['advertiser-id'],
      (request, advertiserId) => ({
        ...request,
        query: { ...request.query, 'advertiser-id': advertiserId },
      }),
    ),
  )

export const OrderItemsStatusSummaryResponseSchema = z.object({
  data: z.object({
    upcoming: z.number(),
    live: z.number(),
    completed: z.number(),
  }),
})

export const GetOrderItemByIdParamsSchema = z.object({
  'order-item-id': QueryIDSchema,
})

export const OrderItemAdvertiserQuerySchema = z.object({
  'advertiser-id': QueryIDSchema,
})

const CreateNonTestCampaignRequestBodySchema = z.object({
  isTest: z.literal(false),
})
const CreateDemoCampaignRequestBodySchema = z.object({
  isTest: z.literal(true),
})
export const CreateCampaignExperimentInputSchema = z.object({
  clonedFromCampaignGroupId: z.number().int().positive(),
})

export const CreateCampaignInputSchema = z.union([
  CreateNonTestCampaignRequestBodySchema,
  CreateDemoCampaignRequestBodySchema,
])

const CreateCampaignRequestBaseSchema = z.object({
  query: OrderItemAdvertiserQuerySchema,
  params: GetOrderItemByIdParamsSchema,
})

export const CreateCampaignRequestSchema = z.union([
  CreateCampaignRequestBaseSchema.extend({
    body: CreateNonTestCampaignRequestBodySchema,
    principal: z.union([T.LoginAsSchema('advertiser'), T.LoginAsSchema('admin')]).and(A.UserSchema),
  })
    .transform(
      createAuthorizeAdvertiserIdTransform(
        request => request.query['advertiser-id'],
        (request, advertiserId) => ({
          ...request,
          query: { 'advertiser-id': advertiserId },
        }),
      ),
    )
    .transform(({ body, params, principal, query }) => ({
      in: {
        advertiserId: query['advertiser-id'],
        isTest: body.isTest,
        orderItemId: params['order-item-id'],
      },
      principal,
    })),
  CreateCampaignRequestBaseSchema.extend({
    body: CreateDemoCampaignRequestBodySchema,
    principal: T.LoginAsSchema('demo').and(A.UserSchema),
  })
    .transform(
      createAuthorizeAdvertiserIdTransform(
        request => request.query['advertiser-id'],
        (request, advertiserId) => ({
          ...request,
          query: { 'advertiser-id': advertiserId },
        }),
      ),
    )
    .transform(({ body, params, principal, query }) => ({
      in: {
        advertiserId: query['advertiser-id'],
        isTest: body.isTest,
        orderItemId: params['order-item-id'],
      },
      principal,
    })),
])

export const CreateCampaignExperimentRequestSchema = CreateCampaignRequestBaseSchema.extend({
  body: CreateCampaignExperimentInputSchema,
  principal: T.LoginAsSchema('admin').and(A.UserSchema),
})
  .transform(
    createAuthorizeAdvertiserIdTransform(
      request => request.query['advertiser-id'],
      (request, advertiserId) => ({
        ...request,
        query: { 'advertiser-id': advertiserId },
      }),
    ),
  )
  .transform(({ body, params, principal, query }) => ({
    in: {
      advertiserId: query['advertiser-id'],
      clonedFromCampaignGroupId: body.clonedFromCampaignGroupId,
      orderItemId: params['order-item-id'],
    },
    principal,
  }))

export const GetOrderItemByIdRequestSchema = z
  .object({
    query: BaseQuerySchema,
    params: GetOrderItemByIdParamsSchema,
    principal: AdvertiserUserPrincipalSchema,
  })
  .transform(
    createAuthorizeAdvertiserIdTransform(
      request => request.query['advertiser-id'],
      (request, advertiserId) => ({
        ...request,
        query: { ...request.query, 'advertiser-id': advertiserId },
      }),
    ),
  )

export const AddBudgetBodySchema = z.object({
  offeringVersionId: z.number().int().positive(),
  advertiserId: z.number().int().positive(),
  budget: z.number().positive(),
  audienceId: z.number().optional(),
  predictionId: z.uuid().optional(),
  isTest: z.boolean(),
})

export const AddBudgetParamsSchema = z.object({
  'order-item-id': z.coerce.number(),
})

/**
 * Transforming here preserves the type union while attaching sourceOrderItemId
 */
function toAddBudgetRequest<
  Body extends z.infer<typeof AddBudgetBodySchema>,
  Principal extends T.Kind<'advertiser'> & A.Kind<'user'>,
>({ body, params, principal }: { body: Body; params: z.infer<typeof AddBudgetParamsSchema>; principal: Principal }) {
  return {
    in: {
      ...body,
      sourceOrderItemId: params['order-item-id'],
    },
    principal,
  }
}

export const AddBudgetRequestSchema = z.union([
  z
    .object({
      body: AddBudgetBodySchema,
      params: AddBudgetParamsSchema,
      principal: T.LoginAsSchema('admin').and(A.UserSchema),
    })
    .transform(authorizeAdvertiserIdBodyTransform)
    .transform(toAddBudgetRequest),
  z
    .object({
      body: AddBudgetBodySchema.extend({ isTest: z.literal(true) }),
      params: AddBudgetParamsSchema,
      principal: T.LoginAsSchema('demo').and(A.UserSchema),
    })
    .transform(authorizeAdvertiserIdBodyTransform)
    .transform(toAddBudgetRequest),
  z
    .object({
      body: AddBudgetBodySchema.extend({ isTest: z.literal(false) }),
      params: AddBudgetParamsSchema,
      principal: T.LoginAsSchema('advertiser').and(A.UserSchema),
    })
    .transform(authorizeAdvertiserIdBodyTransform)
    .transform(toAddBudgetRequest),
])

export const AddBudgetResponseSchema = z.object({
  data: z.object({
    orderId: z.number(),
    orderItemId: z.number(),
  }),
})

const AddBudgetSuccessResponseSchema = z
  .object({
    ok: z.literal(true),
    data: AddBudgetResponseSchema.shape.data,
  })
  .transform(({ data }) => ({ data }))
  .pipe(AddBudgetResponseSchema)

export const AddBudgetResponses = {
  200: AddBudgetSuccessResponseSchema,
  404: z.union([
    E('OFFERING_NOT_FOUND_ERROR'),
    E('OFFERING_NOT_FOUND_DB_ERROR'),
    E('OFFERING_VERSION_NOT_FOUND_DB_ERROR'),
    E('ORDER_ITEM_NOT_FOUND'),
  ]),
  409: z.union([
    E('ORDER_ITEM_TEST_STATE_MISMATCH'),
    E('OFFERING_ADVERTISER_RESTRICTED'),
    E('OFFERING_DEAL_GROUP_NOT_FOUND'),
    E('OFFERING_DELETED'),
    E('OFFERING_OUTSIDE_AVAILABILITY_WINDOW'),
    E('OFFERING_UNPUBLISHED'),
    E('OFFERING_SOLD_OUT'),
    E('OFFERING_INSUFFICIENT_INVENTORY'),
    E('OFFERING_CPM_CHANGED'),
    E('OFFERING_VERSION_MISMATCH'),
  ]),
  500: z.union([
    E('DEALS_CLIENT_AUTHENTICATION_ERROR'),
    E('DEALS_CLIENT_AUTHORIZATION_ERROR'),
    E('DEALS_CLIENT_BAD_GATEWAY_ERROR'),
    E('DEALS_CLIENT_INPUT_VALIDATION_ERROR'),
    E('DEALS_CLIENT_INTERNAL_SERVER_ERROR'),
    E('DEALS_CLIENT_NOT_FOUND_ERROR'),
    E('DEALS_CLIENT_RESPONSE_PARSE_ERROR'),
    E('DEALS_CLIENT_UNAVAILABLE_ERROR'),
    E('DEALS_CLIENT_UNEXPECTED_RESPONSE_ERROR'),
    E('OFFERING_DEAL_GROUP_EMPTY'),
    E('OFFERING_VERSION_PRIVATE_MARKETPLACE_GROUP_ACCESS_DB_ERROR'),
    E('OFFERING_VERSIONS_X_PRIVATE_MARKETPLACE_GROUPS_FETCH_DB_ERROR'),
    E('PURCHASE_RESTRICTION_DB_ERROR'),
    E('SHOPPER_GRAPH_CLIENT_ERROR'),
    E('SHOPPER_GRAPH_RESPONSE_PARSE_ERROR'),
    E('ADD_BUDGET_DB_ERROR'),
    E('GARY_BASIC_AUTH_CREDENTIALS_ERROR'),
    E('GARY_REST_API_ERROR'),
  ]),
}

export const CreateCampaignResponseSchema = z.object({
  data: z.object({ campaignGroupId: z.number(), productId: z.number() }),
})

export const GetOrderItemByIdResponseSchema = z.object({
  data: z.array(OrderItemSchema),
})

// Campaign mappings (mounted under the same /order-items prefix)
export const OrderItemsXCampaignGroupsQuerySchema = z
  .object({
    'advertiser-id': QueryIDSchema.optional(),
    'campaign-group-id': QueryIDSchema.optional(),
    'order-item-id': QueryIDSchema.optional(),
    'with-order-info': z.boolean().optional(),
  })
  .refine(data => data['campaign-group-id'] || data['order-item-id'], {
    message: 'Either campaign-group-id or order-item-id is required',
  })

export const FindOrderItemsXCampaignGroupsRequestSchema = z.union([
  z
    .object({
      query: OrderItemsXCampaignGroupsQuerySchema.and(z.object({ 'advertiser-id': QueryIDSchema })),
      principal: AdvertiserUserPrincipalSchema,
    })
    .transform(
      createAuthorizeAdvertiserIdTransform(
        request => request.query['advertiser-id'],
        (request, advertiserId) => ({
          in: {
            campaignGroupIds: request.query['campaign-group-id'] ? [request.query['campaign-group-id']] : undefined,
            orderItemIds: request.query['order-item-id'] ? [request.query['order-item-id']] : undefined,
            withOrderInfo: request.query['with-order-info'],
            advertiserId,
          },
          principal: request.principal,
        }),
      ),
    ),
  z
    .object({
      query: OrderItemsXCampaignGroupsQuerySchema.and(z.object({ 'advertiser-id': z.never().optional() })),
      principal: T.KindSchema('none').and(A.UserSchema),
    })
    .transform(request => ({
      in: {
        campaignGroupIds: request.query['campaign-group-id'] ? [request.query['campaign-group-id']] : undefined,
        orderItemIds: request.query['order-item-id'] ? [request.query['order-item-id']] : undefined,
        withOrderInfo: request.query['with-order-info'],
      },
      principal: request.principal,
    })),
])

export const OrderItemsXCampaignGroupsResponseSchema = z.array(
  z.object({
    orderItemId: z.number(),
    campaignGroupId: z.number(),
  }),
)

// Errors
export const GetOrderItemsErrorResponseSchema = z.union([
  z.object({
    customCode: z.literal('CAMPAIGN_GROUPS_BY_EXTERNAL_REF_IDS_FETCH_ERROR'),
    message: z.string(),
  }),
  z.object({
    customCode: z.literal('ORDER_ITEM_MISSING_LATEST_VERSION'),
    message: z.string(),
  }),
  z.object({
    customCode: z.literal('OFFERING_NOT_FOUND_ERROR'),
    message: z.string(),
  }),
  z.object({
    customCode: z.literal('OFFERING_NOT_FOUND_DB_ERROR'),
    message: z.string(),
  }),
  z.object({
    customCode: z.literal('BILLING_MARGINS_FETCH_ERROR'),
    message: z.string(),
  }),
  z.object({
    customCode: z.literal('GARY_REST_API_ERROR'),
    message: z.string(),
  }),
  z.object({
    customCode: z.literal('GARY_BASIC_AUTH_CREDENTIALS_ERROR'),
    message: z.string(),
  }),
  z.object({
    customCode: z.literal('OFFERING_VERSIONS_X_PRIVATE_MARKETPLACE_GROUPS_FETCH_DB_ERROR'),
    message: z.string(),
  }),
])

export type GetOrderItemsErrorResponse = z.infer<typeof GetOrderItemsErrorResponseSchema>

export const GET_ORDER_ITEMS_ERROR_RESPONSES: Record<
  GetOrderItemsErrorResponse['customCode'],
  GetOrderItemsErrorResponse
> = {
  CAMPAIGN_GROUPS_BY_EXTERNAL_REF_IDS_FETCH_ERROR: {
    customCode: 'CAMPAIGN_GROUPS_BY_EXTERNAL_REF_IDS_FETCH_ERROR',
    message: 'Failed to fetch campaign groups by external ref ids',
  },
  ORDER_ITEM_MISSING_LATEST_VERSION: {
    customCode: 'ORDER_ITEM_MISSING_LATEST_VERSION',
    message: 'Order item is missing latest offering version',
  },
  OFFERING_NOT_FOUND_ERROR: {
    customCode: 'OFFERING_NOT_FOUND_ERROR',
    message: 'Offering snapshot not found',
  },
  OFFERING_NOT_FOUND_DB_ERROR: {
    customCode: 'OFFERING_NOT_FOUND_DB_ERROR',
    message: 'Offering not found',
  },
  BILLING_MARGINS_FETCH_ERROR: {
    customCode: 'BILLING_MARGINS_FETCH_ERROR',
    message: 'Failed to fetch billing margins',
  },
  GARY_REST_API_ERROR: {
    customCode: 'GARY_REST_API_ERROR',
    message: 'Gary REST API error',
  },
  GARY_BASIC_AUTH_CREDENTIALS_ERROR: {
    customCode: 'GARY_BASIC_AUTH_CREDENTIALS_ERROR',
    message: 'Gary Basic Auth credentials not configured',
  },
  OFFERING_VERSIONS_X_PRIVATE_MARKETPLACE_GROUPS_FETCH_DB_ERROR: {
    customCode: 'OFFERING_VERSIONS_X_PRIVATE_MARKETPLACE_GROUPS_FETCH_DB_ERROR',
    message: 'Failed to fetch offering version pmp groups',
  },
}

export type BaseOrderItem = z.infer<typeof BaseOrderItemSchema>
export type OrderItem = z.infer<typeof OrderItemSchema>
export type OrderItemSummary = z.infer<typeof OrderItemSummarySchema>
export type OrderItemsStatusSummary = z.infer<typeof OrderItemsStatusSummaryResponseSchema>['data']
export type OrderItemsGetRequest = z.infer<typeof OrderItemsGetRequestSchema>
export type OrderItemsGetResponse = z.infer<typeof OrderItemsGetResponseSchema>
export type CreateCampaignResponse = z.infer<typeof CreateCampaignResponseSchema>
export type OrderItemsStatusSummaryRequest = z.infer<typeof OrderItemsStatusSummaryRequestSchema>
export type OrderItemsSummaryRequest = z.infer<typeof OrderItemsSummaryRequestSchema>
